import { DatePicker, InputNumber, Radio, theme } from 'antd';
import dayjs from 'dayjs';
import { useRef, useState } from 'react';
import { Flex, Form, Select, Typography } from 'zui/Antd';

const { Text } = Typography;

const DURATION_UNITS = [
	{ label: 'Days', value: 'day' },
	{ label: 'Weeks', value: 'week' },
	{ label: 'Months', value: 'month' },
	{ label: 'Years', value: 'year' }
];

/**
 * Reusable validity picker: Permanent / Duration / Custom date range.
 *
 * @param {boolean} isPermanent - Current permanent state
 * @param {dayjs|null} startDate - Current start date
 * @param {dayjs|null} endDate - Current end date
 * @param {function} onChange - Called with ({ isPermanent, startDate, endDate })
 * @param {string} size - 'small' | 'middle' (default 'middle')
 * @param {boolean} showLabel - Show "Access Validity" label (default false)
 */
const ValidityPicker = ({ isPermanent, startDate, endDate, onChange, size = 'middle', showLabel = false }) => {
	const {
		token: { fontWeightStrong, paddingSM, colorBgLayout }
	} = theme.useToken();

	const endDateRef = useRef(null);
	const [durationMode, setDurationMode] = useState(() => {
		if (isPermanent) return null;
		return 'duration';
	});
	const [durationAmount, setDurationAmount] = useState(1);
	const [durationUnit, setDurationUnit] = useState('week');

	const applyDuration = (amount, unit) => {
		const start = dayjs();
		const end = start.add(amount, unit);
		onChange({ isPermanent: false, startDate: start, endDate: end });
	};

	const handleTypeChange = (type) => {
		if (type === 'permanent') {
			setDurationMode(null);
			onChange({ isPermanent: true, startDate: null, endDate: null });
		} else {
			setDurationMode('duration');
			applyDuration(durationAmount, durationUnit);
		}
	};

	const handleDurationModeChange = (mode) => {
		setDurationMode(mode);
		if (mode === 'duration') {
			applyDuration(durationAmount, durationUnit);
		} else {
			onChange({ isPermanent: false, startDate: dayjs(), endDate: null });
			setTimeout(() => endDateRef.current?.focus(), 150);
		}
	};

	return (
		<Flex vertical gap={paddingSM} style={{ background: colorBgLayout, padding: paddingSM, borderRadius: 8 }}>
			{showLabel && <Text strong>Access Validity</Text>}

			<Radio.Group
				value={isPermanent ? 'permanent' : 'time-based'}
				onChange={(e) => handleTypeChange(e.target.value)}
				size={size}
				style={{ fontWeight: 400 }}
			>
				<Radio value='permanent'>Permanent</Radio>
				<Radio value='time-based'>Time-based</Radio>
			</Radio.Group>

			{!isPermanent && (
				<>
					<Radio.Group
						value={durationMode}
						onChange={(e) => handleDurationModeChange(e.target.value)}
						size={size}
						style={{ fontWeight: 400 }}
					>
						<Radio value='duration'>Duration</Radio>
						<Radio value='custom'>Custom</Radio>
					</Radio.Group>

					{durationMode === 'duration' && (
						<>
							<Flex gap={paddingSM}>
								<InputNumber
									min={1}
									max={99}
									value={durationAmount}
									onChange={(val) => {
										const amount = val || 1;
										setDurationAmount(amount);
										applyDuration(amount, durationUnit);
									}}
									style={{ width: 80, fontWeight: 400 }}
									size={size}
								/>
								<Select
									value={durationUnit}
									onChange={(unit) => {
										setDurationUnit(unit);
										applyDuration(durationAmount, unit);
									}}
									options={DURATION_UNITS}
									style={{ flex: 1, fontWeight: 400 }}
									size={size}
								/>
							</Flex>
							{startDate && endDate && (
								<Text type='secondary' style={{ fontSize: 12 }}>
									{startDate.format('DD/MM/YYYY hh:mm A')} — {endDate.format('DD/MM/YYYY hh:mm A')}
								</Text>
							)}
						</>
					)}

					{durationMode === 'custom' && (
						<Flex vertical gap={paddingSM}>
							<Form.Item
								label='Start Date'
								style={{ fontWeight: fontWeightStrong, margin: 0 }}
							>
								<DatePicker
									value={startDate}
									onChange={(date) => onChange({ isPermanent: false, startDate: date, endDate })}
									format='DD/MM/YYYY hh:mm A'
									showTime
									disabledDate={(current) => current && current.isBefore(dayjs(), 'day')}
									disabledTime={(current) => {
										if (!current || !current.isSame(dayjs(), 'day')) return {};
										const now = dayjs();
										return {
											disabledHours: () =>
												Array.from({ length: 24 }, (_, i) => i).filter((h) => h < now.hour()),
											disabledMinutes: (h) =>
												h === now.hour()
													? Array.from({ length: 60 }, (_, i) => i).filter(
															(m) => m < now.minute()
													  )
													: []
										};
									}}
									style={{ width: '100%', fontWeight: 400 }}
									size={size}
									allowClear={false}
								/>
							</Form.Item>
							<Form.Item
								label='End Date'
								style={{ fontWeight: fontWeightStrong, margin: 0 }}
							>
								<DatePicker
									ref={endDateRef}
									value={endDate}
									onChange={(date) => onChange({ isPermanent: false, startDate, endDate: date })}
									format='DD/MM/YYYY hh:mm A'
									showTime
									disabledDate={(current) => {
										if (!current) return false;
										const s = startDate || dayjs();
										return current.isBefore(s, 'day');
									}}
									disabledTime={(current) => {
										const s = startDate || dayjs();
										if (!current || !current.isSame(s, 'day')) return {};
										return {
											disabledHours: () =>
												Array.from({ length: 24 }, (_, i) => i).filter((h) => h < s.hour()),
											disabledMinutes: (h) =>
												h === s.hour()
													? Array.from({ length: 60 }, (_, i) => i).filter(
															(m) => m <= s.minute()
													  )
													: []
										};
									}}
									style={{ width: '100%', fontWeight: 400 }}
									size={size}
									allowClear={false}
								/>
							</Form.Item>
						</Flex>
					)}
				</>
			)}
		</Flex>
	);
};

export default ValidityPicker;
